import { useCallback, useEffect, useRef, useState } from 'react'
import { registerEventPaths, registerVenuePaths } from './staticPaths'
import { supabase } from './supabase'
import { hostedByName, hostedByNames } from './hostedBy'
import { PUBLIC_EVENT_COLUMNS } from './eventColumns'
import type { EventRow, PendingEventRow } from '../types'
import { primeVendorTypes, type VendorType } from './vendorTypes'

// ---- Derived views of the event rows ----

// Who puts an event on, read off the venue string (and, for an organiser
// layered on top of a venue, off the event's own title too). The resolution
// rules - the address strip, the spellings that collapse into one place, the
// venue values that name nobody, the organiser overlay - are their own file,
// because there are enough of them to need explaining. See lib/hostedBy.
export { hostedByName, hostedByNames }

export interface HostedByCount {
  name: string
  count: number
}

/**
 * Hosts with at least `min` events, A to Z. An event can carry more than one
 * name (hostedByNames - a venue plus an organiser layered on top of it), and
 * each one gets its own count here, so a Tile Tuesday row at Palmera House
 * counts toward BOTH Palmera House's total and Hill City Mahj's.
 *
 * A threshold because the tail is long and useless as a checkbox: 150 distinct
 * venues, 84 of them with a single event. Those are reachable by search, which
 * matches the venue too.
 *
 * Alphabetical rather than busiest-first (Lisa, 7/28): at 47 rows people are
 * scanning for a name they already have in mind, and A-Z is the only order you
 * can scan without reading every line.
 *
 * `eligibilityEvents` splits WHO gets a box from what its count says. The
 * drawer counts over the upcoming scope, and judging the threshold on that
 * same scope starved venues that run a few big one-offs: the Lynchburg
 * Amphitheater had 6 rows all-time but only 2 still ahead, so it never earned
 * a checkbox and Andrew had to reach it through search (2026-08-04). Pass the
 * ALL-events rows here and the threshold reads a venue's history while the
 * count keeps telling the truth about what is left to attend. Names with
 * nothing in scope still drop entirely - a box that empties the calendar is
 * the thing the drawer must never offer.
 */
export function hostedByCounts(
  events: Pick<EventRow, 'venue' | 'name'>[],
  min = 3,
  eligibilityEvents?: Pick<EventRow, 'venue' | 'name'>[],
): HostedByCount[] {
  const m = new Map<string, number>()
  for (const e of events) {
    for (const name of hostedByNames(e.venue, e.name)) {
      m.set(name, (m.get(name) ?? 0) + 1)
    }
  }
  let eligible = m
  if (eligibilityEvents) {
    eligible = new Map()
    for (const e of eligibilityEvents) {
      for (const name of hostedByNames(e.venue, e.name)) {
        eligible.set(name, (eligible.get(name) ?? 0) + 1)
      }
    }
  }
  return [...m.entries()]
    .filter(([name]) => (eligible.get(name) ?? 0) >= min)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

// The search matcher and the typeahead live in lib/search, which imports no
// React so an eval script can run them over the live rows.

// Public: approved events only (enforced by RLS too), and only the columns a
// card can render. The named list rather than '*' is the client half of keeping
// submitted_by off the public wire - see lib/eventColumns for the whole story.
export interface EventsSnapshot {
  generatedAt: string
  /** Eastern dates, inclusive, that dist/events.json actually covers. */
  from: string
  to: string
  events: EventRow[]
  /** For /vendors (lib/vendorTypes). Absent when the function could not read them. */
  vendorTypes?: VendorType[]
  eventVendorTypes?: { event_id: string; vendor_type: string }[]
  eventVendorDeadlines?: { event_id: string; deadline: string }[]
}

/**
 * Rows for the calendar, off the CDN first and Supabase second.
 *
 * WHAT THIS USED TO DO, AND WHY IT WAS SLOW. It looped `range(from, from+999)`
 * and AWAITED each page before asking for the next, then called setEvents once
 * at the end - so the app painted nothing until every row was in. Measured on
 * production with 2891 approved rows: the bundle was parsed and sitting idle at
 * 680ms, then three requests ran back to back (333-1160ms, 1161-1824ms,
 * 1826-2418ms), each starting within 2ms of the one before finishing. Two full
 * seconds of the load was the app waiting on itself.
 *
 * Shrinking the payload does not help. Each of those requests cost 592-827ms
 * whatever its size: a 98KB fourteen-day window measured 578ms against a full
 * 137KB page's 743ms. The cost is the round trip, so the fix is fewer of them,
 * not smaller ones.
 *
 * THREE CHANGES, IN THE ORDER THEY PAY OFF:
 *
 *   1. BOOT FROM THE CDN. `dist/events.json` is written at build time by
 *      scripts/generate-seo-pages.mjs, off the fetch it was already doing for
 *      the /e/ and /day/ pages, so it costs nothing extra to produce. On the
 *      same page load that spent 827ms on one Supabase call, Netlify's edge
 *      returned the 227KB JS bundle in 38ms. The app paints from this.
 *
 *   2. DO NOT WAIT FOR EVERYTHING TO PAINT SOMETHING. `loading` now means "I
 *      have nothing to show", so it clears on whichever source answers first.
 *      `settled` is the separate signal for "the authoritative load finished",
 *      which is what the effects that need the FULL set have to wait on.
 *
 *   3. PAGE IN PARALLEL. The Supabase pass asks the first page for an exact
 *      count, then fires every remaining page at once instead of walking them.
 *      Three serial trips become two waves.
 *
 * Supabase stays the source of truth. The snapshot is only as fresh as the last
 * deploy, so it is always overwritten by the live rows when they land, and a
 * visitor who arrives between a new event being approved and the next build sees
 * it appear a second or so in. That is the trade: instant and a beat stale, over
 * correct and three seconds blank.
 */
export function useApprovedEvents() {
  const [events, setEvents] = useState<EventRow[]>([])
  /** Nothing to render yet. Clears on the FIRST source to answer. */
  const [loading, setLoading] = useState(true)
  /** The Supabase pass finished. Effects needing every row gate on this. */
  const [settled, setSettled] = useState(false)
  /** What the snapshot covers, until the live rows make it irrelevant. */
  const [snapshotRange, setSnapshotRange] = useState<{ from: string; to: string } | null>(null)

  // Bumped per refetch so a slow response from a previous run cannot land on
  // top of a newer one. The snapshot and the live pass race by design, so
  // "whoever finishes last wins" is not good enough on its own.
  const run = useRef(0)

  const refetch = useCallback(async () => {
    const token = ++run.current
    setLoading(true)
    setSettled(false)

    // The snapshot never overwrites live rows: it is the older of the two by
    // construction, so once `live` has landed this is a no-op.
    let live = false

    const snapshot = (async (): Promise<boolean> => {
      try {
        const res = await fetch('/events.json', { cache: 'no-cache' })
        if (!res.ok) return false
        const snap = (await res.json()) as EventsSnapshot
        if (token !== run.current || live) return false
        if (!Array.isArray(snap.events) || snap.events.length === 0) return false
        setEvents(snap.events)
        setSnapshotRange({ from: snap.from, to: snap.to })
        if (snap.vendorTypes && snap.eventVendorTypes && snap.eventVendorDeadlines) {
          primeVendorTypes(snap.vendorTypes, snap.eventVendorTypes, snap.eventVendorDeadlines)
        }
        setLoading(false)
        return true
      } catch {
        // A missing or malformed snapshot is not an error worth surfacing - the
        // Supabase pass below is the real load and covers it. This only ever
        // costs the visitor the speed-up.
        return false
      }
    })()

    const PAGE = 1000
    const page = (from: number) =>
      supabase
        .from('lyh_events')
        .select(PUBLIC_EVENT_COLUMNS)
        .eq('status', 'approved')
        .order('start_time', { ascending: true })
        .range(from, from + PAGE - 1)

    // First page carries the total, which is what lets the rest go out at once.
    const first = await supabase
      .from('lyh_events')
      .select(PUBLIC_EVENT_COLUMNS, { count: 'exact' })
      .eq('status', 'approved')
      .order('start_time', { ascending: true })
      .range(0, PAGE - 1)

    if (token !== run.current) return
    if (first.error) {
      console.error('load approved events', first.error)
      // The snapshot may still rescue the render, so let it finish before
      // deciding there is nothing to show.
      const painted = await snapshot
      if (token === run.current) {
        if (painted) setLoading(false)
        setSettled(true)
      }
      return
    }

    let all = (first.data as EventRow[]) ?? []
    const total = first.count ?? 0

    if (total > all.length) {
      const offsets: number[] = []
      for (let from = PAGE; from < total; from += PAGE) offsets.push(from)
      const rest = await Promise.all(offsets.map(page))
      if (token !== run.current) return
      for (const r of rest) {
        if (r.error) {
          console.error('load approved events', r.error)
          break
        }
        all = all.concat((r.data as EventRow[]) ?? [])
      }
    }

    // Backstop for the bug this repo has already shipped once (1,271 rows
    // arriving as 1,000 and the calendar quietly ending in September). If the
    // count was wrong or rows were added mid-flight, walk the tail the old way
    // rather than trusting the arithmetic.
    for (;;) {
      if (all.length >= total && all.length % PAGE !== 0) break
      const { data, error } = await page(all.length)
      if (token !== run.current) return
      if (error) {
        console.error('load approved events', error)
        break
      }
      const batch = (data as EventRow[]) ?? []
      if (batch.length === 0) break
      all = all.concat(batch)
    }

    // Pages were fetched concurrently, so a row inserted between two of them
    // could land out of order at a boundary. Cheap to make certain.
    all.sort((a, b) => a.start_time.localeCompare(b.start_time))

    live = true
    setEvents(all)
    setSnapshotRange(null)
    setLoading(false)
    setSettled(true)
  }, [])

  useEffect(() => {
    refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Repeating events share one static page, and which url that is depends on
  // the whole set rather than on any single row, so the map is rebuilt whenever
  // the set changes. Without this the app's own hrefs point at the pre-collapse
  // url and every internal click costs a 301.
  useEffect(() => {
    registerEventPaths(events)
    // Which venues have a generated page, so the modal only links the ones that
    // do. Same set, same trigger.
    registerVenuePaths(events)
  }, [events])

  return { events, loading, settled, snapshotRange, refetch }
}

/**
 * Admin: the pending approval queue, read through an RPC rather than the table.
 *
 * The queue is the one screen that has to see `submitted_by` - it is how a
 * moderator tells a community submission from a scrape, and how they reach the
 * person who sent it. But that column is no longer selectable by `anon` OR
 * `authenticated`, and it cannot be granted to just the admin, because column
 * privileges are per role and this project has open signups plus anonymous
 * sign-in: "authenticated" is a stranger with a JWT, not Andrew.
 *
 * So `lyh_pending_events()` is security definer, runs as the table owner, and
 * gates itself on the same predicate the RLS policies use. A non-admin calling
 * it gets an empty array, which is what the row policy gave them before.
 */
export function usePendingEvents(enabled: boolean) {
  const [pending, setPending] = useState<PendingEventRow[]>([])

  const refetch = useCallback(async () => {
    if (!enabled) {
      setPending([])
      return
    }
    const { data, error } = await supabase.rpc('lyh_pending_events')
    if (error) console.error('load pending events', error)
    setPending((data as PendingEventRow[]) ?? [])
  }, [enabled])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { pending, refetch }
}
